import classNames from 'classnames'
import { type ReactNode } from 'react'
import { Size } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '@opallabs/lapidary'
import { barColors } from '../resolveItemColor'
import { CalendarSizes } from '../sizes'
import { type CalendarItem } from '../types'
import { calendarKindGlyph } from './panelData'
import * as styles from './styles'

type SharedRowProps = Readonly<{
  /** The colour the grid draws the item in. */
  color?: string
  item: CalendarItem
  onOpen: (item: CalendarItem) => void
  /** The days the row covers, when it covers more than one: "Jul 1 – Jul 3". */
  range?: string
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
}>

/**
 * The marks at the trailing edge of a row. A size up from the grid's, which has
 * to fit them on a chip twenty pixels tall: a row has the room, and the marks
 * are what a person scans a list by.
 */
const MARK_SIZE = Size.Icon

/** The colour bar down the leading edge of a row. */
const stripe = (color: string | undefined) => {
  const derived = color ? barColors(color) : null

  return (
    <span
      className={styles.selectors.rowStatus}
      style={derived ? { backgroundColor: derived.border } : undefined}
    />
  )
}

/**
 * The trailing edge of a row, in the order the design reads it: when it goes
 * out, what state it is in, and whether anyone else can see it.
 */
const cluster = (
  item: CalendarItem,
  renderStatusIcon:
    | ((item: CalendarItem, size: number) => ReactNode)
    | undefined,
  shouldShowTime: boolean
) => (
  <span className={styles.selectors.rowTime}>
    {shouldShowTime && item.time}
    {renderStatusIcon?.(item, MARK_SIZE)}
    {item.isPrivate && (
      <Icon ariaLabel="Private" glyph="gem-locked" size={MARK_SIZE} />
    )}
  </span>
)

export type CalendarMomentRowProps = SharedRowProps &
  Readonly<{
    /**
     * The moment's cover picture. Without one the row is a placeholder: it
     * wears the sticky yellow a moment wears in the week view before anyone
     * has given it a picture, and a blank tile keeps the picture's place so
     * every moment row has the same shape.
     */
    imageUrl?: string | null
  }>

/**
 * A moment on a day panel.
 *
 * The tile beside the name is the moment's cover, which the host fetches for
 * the day the panel is showing. The mark on its corner says what kind of thing
 * the row is, the same way it does on the item panel's hero.
 */
export const CalendarMomentRow: React.FC<CalendarMomentRowProps> = ({
  color,
  imageUrl,
  item,
  onOpen,
  range,
  renderStatusIcon,
}) => {
  const theme = useTheme()
  const hasPicture = typeof imageUrl === 'string'

  return (
    <button
      className={classNames(styles.selectors.momentRow, {
        [styles.stateVariables.placeholder]: !hasPicture,
      })}
      style={hasPicture ? styles.rowFillStyle(color) : undefined}
      type="button"
      onClick={() => onOpen(item)}
    >
      {stripe(color)}
      <span className={styles.selectors.momentThumb}>
        <span className={styles.selectors.momentThumbSurface}>
          {hasPicture && (
            <img
              alt=""
              className={styles.selectors.momentThumbImage}
              src={imageUrl}
            />
          )}
        </span>
        <span className={styles.selectors.momentBadge}>
          <Icon
            ariaHidden={true}
            glyph={calendarKindGlyph(theme, 'moment')}
            size={Size.Icon}
          />
        </span>
      </span>
      <span className={styles.selectors.rowBody}>
        <span className={styles.selectors.rowTitle}>{item.title}</span>
        {range !== undefined && (
          <span className={styles.selectors.rowRange}>{range}</span>
        )}
      </span>
      {cluster(item, renderStatusIcon, false)}
    </button>
  )
}
CalendarMomentRow.displayName = 'Calendar: CalendarMomentRow'

export type CalendarContentRowProps = SharedRowProps &
  Readonly<{
    accountName?: string
    /** The moment the content belongs to, named. */
    momentTitle?: string
    renderChannelIcon?: (item: CalendarItem) => ReactNode
  }>

/**
 * One piece of content, wherever a panel lists it: on its day, or under the
 * moment it belongs to.
 *
 * Under the name, a line for each question the row can answer — how long it
 * runs for, what it is part of, and where it goes out. A line with no answer
 * is left out rather than drawn empty; the line that says where it goes out is
 * the exception, because a channel mark on its own reads as a line the panel
 * failed to fill. It names the gap in the grid's words instead.
 */
export const CalendarContentRow: React.FC<CalendarContentRowProps> = ({
  accountName,
  color,
  item,
  momentTitle,
  onOpen,
  range,
  renderChannelIcon,
  renderStatusIcon,
}) => {
  const theme = useTheme()
  const channelMark = renderChannelIcon?.(item)
  const isMultiDay = range !== undefined

  return (
    <button
      className={classNames(styles.selectors.row, {
        [styles.stateVariables.multiDay]: isMultiDay,
      })}
      type="button"
      onClick={() => onOpen(item)}
    >
      {stripe(color)}
      <span className={styles.selectors.rowBody}>
        <span className={styles.selectors.rowLine}>
          <span className={styles.selectors.rowTitle}>{item.title}</span>
          {cluster(item, renderStatusIcon, true)}
        </span>
        <span className={styles.selectors.rowDetails}>
          {isMultiDay && (
            <span className={styles.selectors.rowRange}>{range}</span>
          )}
          {momentTitle !== undefined && (
            <span className={styles.selectors.rowParent}>
              <span className={styles.selectors.rowMark}>
                <Icon
                  ariaHidden={true}
                  glyph={calendarKindGlyph(theme, 'moment')}
                  size={CalendarSizes.cardMomentIconSize}
                />
              </span>
              <span className={styles.selectors.truncate}>{momentTitle}</span>
            </span>
          )}
          {(channelMark || accountName !== undefined) && (
            <span className={styles.selectors.rowMeta}>
              {channelMark}
              <span className={styles.selectors.truncate}>
                {accountName ?? 'No Account'}
              </span>
            </span>
          )}
        </span>
      </span>
    </button>
  )
}
CalendarContentRow.displayName = 'Calendar: CalendarContentRow'
